/**
 * <color-upsell> — single upsell card behavior (product page; the cart's
 * "Upotpunite izgled" rows are assets/cart-look.js).
 *
 * Responsibilities:
 *  - Size <select> change updates the form's variant id.
 *  - Form submit posts to /cart/add.js, then opens the cart drawer.
 *  - Color swatch click swaps the card content inline (image, title, price,
 *    sizes, variant id) without navigating away. Falls back to navigation
 *    on fetch failure.
 */

const SIZE_NAMES = ['velicina', 'size', 'broj'];

function normalizeOptionName(raw) {
  const str = typeof raw === 'string' ? raw : raw?.name || '';
  return str
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .trim()
    .toLowerCase();
}

class ColorUpsell extends HTMLElement {
  connectedCallback() {
    this.#bind();
    this.defaultAtcHtml = this.atcBtn?.innerHTML ?? '';
    this.outOfStockLabel = this.atcBtn?.dataset.outOfStockLabel ?? '';
    this.#updateAtcState();
    this.#attachListeners();
  }

  #bind() {
    this.form = this.querySelector('[data-upsell-form]');
    this.variantInput = this.querySelector('[data-upsell-variant-id]');
    this.sizeSelect = this.querySelector('[data-upsell-size]');
    this.atcBtn = this.querySelector('[data-upsell-atc]');
    this.sizeOptionIndex = parseInt(this.dataset.sizeOptionIndex, 10);
  }

  #attachListeners() {
    this.sizeSelect?.addEventListener('change', () => this.#onSizeChange());
    this.form?.addEventListener('submit', (e) => this.#onSubmit(e));
    this.querySelectorAll('[data-upsell-swatch]').forEach((sw) => {
      sw.addEventListener('click', (e) => this.#onSwatchClick(e, sw));
    });
  }

  #onSizeChange() {
    if (!this.sizeSelect || !this.variantInput) return;
    const id = this.sizeSelect.value;
    if (id) this.variantInput.value = id;
    this.#updateAtcState();
  }

  #updateAtcState() {
    if (!this.atcBtn) return;
    const id = this.variantInput?.value;
    const sizeRequired = this.sizeOptionIndex >= 0 && this.sizeSelect;
    const selectedOpt = sizeRequired ? this.sizeSelect.options[this.sizeSelect.selectedIndex] : null;
    const sizeChosen = !sizeRequired || (this.sizeSelect.value && !selectedOpt?.disabled);
    const selectedIsSoldOut = sizeRequired && this.sizeSelect.value && selectedOpt?.disabled;

    if (selectedIsSoldOut && this.outOfStockLabel) {
      this.atcBtn.textContent = this.outOfStockLabel;
      this.atcBtn.disabled = true;
    } else {
      if (this.atcBtn.innerHTML !== this.defaultAtcHtml) {
        this.atcBtn.innerHTML = this.defaultAtcHtml;
      }
      this.atcBtn.disabled = !id || !sizeChosen;
    }
  }

  async #onSubmit(e) {
    e?.preventDefault();
    if (this.atcBtn?.disabled) return;
    const id = this.variantInput?.value;
    if (!id) return;

    if (this.atcBtn) this.atcBtn.disabled = true;

    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(id), quantity: 1 }] }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.description || 'add to cart failed');
      }

      document.dispatchEvent(new CustomEvent('cart:refresh'));
      document.dispatchEvent(new CustomEvent('cart:open'));
    } catch (err) {
      console.error('[color-upsell] add to cart failed', err);
    } finally {
      this.#updateAtcState();
    }
  }

  async #onSwatchClick(e, sw) {
    const handle = sw.dataset.siblingHandle;
    if (!handle) return;
    e.preventDefault();
    if (this._loading) return;
    if (handle === this.dataset.productHandle) return;

    this.#setLoading(true);
    try {
      const res = await fetch(`/products/${handle}.js`, { headers: { Accept: 'application/json' } });
      if (!res.ok) throw new Error(`fetch failed: ${res.status}`);
      const data = await res.json();
      this.#applyProduct(data, sw);
    } catch (err) {
      console.error('[color-upsell] swatch swap failed', err);
      // Fallback: navigate to the sibling product as before
      if (sw.tagName === 'A' && sw.href) window.location.href = sw.href;
    } finally {
      this.#setLoading(false);
    }
  }

  #setLoading(on) {
    this._loading = on;
    this.style.opacity = on ? '0.6' : '';
    this.style.pointerEvents = on ? 'none' : '';
    this.style.transition = 'opacity 120ms ease';
  }

  #applyProduct(data, swatchEl) {
    const sizeIdx = this.#findSizeIndex(data);
    const firstAvailable = (data.variants || []).find((v) => v.available) || data.variants?.[0];

    // Host data attrs
    this.dataset.productHandle = data.handle;
    this.dataset.productUrl = data.url;
    this.dataset.sizeOptionIndex = String(sizeIdx);

    // Image
    const img = this.querySelector('[data-upsell-image-link] img');
    if (img && data.featured_image) {
      img.src = this.#thumbUrl(data.featured_image, 200);
      img.removeAttribute('srcset');
      img.alt = data.title;
    }

    // Links
    const imgLink = this.querySelector('[data-upsell-image-link]');
    const titleLink = this.querySelector('[data-upsell-title-link]');
    if (imgLink) imgLink.href = data.url;
    if (titleLink) {
      titleLink.href = data.url;
      titleLink.textContent = data.title;
    }

    // Price + compare
    this.#renderPrice(firstAvailable);

    // Active swatch state
    if (swatchEl) {
      this.querySelectorAll('[data-upsell-swatch]').forEach((el) => {
        el.classList.remove('is-active');
        el.removeAttribute('aria-current');
      });
      swatchEl.classList.add('is-active');
      swatchEl.setAttribute('aria-current', 'true');
    }

    // Size select + variant id
    this.#renderSizes(data, sizeIdx);
    this.#bind();
    if (this.variantInput && firstAvailable) this.variantInput.value = String(firstAvailable.id);
    this.#updateAtcState();
  }

  #renderPrice(variant) {
    const wrap = this.querySelector('[data-upsell-price-wrap]');
    if (!wrap || !variant) return;
    const price = variant.price;
    const compare = variant.compare_at_price;
    const onSale = compare && compare > price;
    wrap.innerHTML = `
      <span class="text-(--fg-2)" data-upsell-price>${this.#formatMoney(price)}</span>
      ${onSale ? `<span class="line-through text-(--fg-3) text-xs" data-upsell-compare>${this.#formatMoney(compare)}</span>` : ''}
    `;
  }

  #renderSizes(data, sizeIdx) {
    const form = this.querySelector('[data-upsell-form]');
    if (!form) return;
    form.querySelector('[data-upsell-size]')?.remove();
    form.querySelectorAll('label.visually-hidden').forEach((l) => l.remove());

    if (sizeIdx < 0) return;

    const placeholder = this.dataset.sizePlaceholder || '';
    const soldOutSuffix = this.dataset.soldOutSuffix || '';

    const label = document.createElement('label');
    label.className = 'visually-hidden';
    label.setAttribute('for', `upsell-size-${data.id}`);
    label.textContent = placeholder || 'Size';

    const select = document.createElement('select');
    select.id = `upsell-size-${data.id}`;
    select.dataset.upsellSize = '';
    if (placeholder) select.dataset.placeholder = placeholder;
    if (soldOutSuffix) select.dataset.soldOutSuffix = soldOutSuffix;
    select.className = 'border border-(--border-default) px-2 py-2 text-xs flex-1 min-w-0';

    const blank = document.createElement('option');
    blank.value = '';
    blank.disabled = true;
    blank.selected = true;
    blank.textContent = placeholder;
    select.appendChild(blank);

    (data.variants || []).forEach((v) => {
      const opt = document.createElement('option');
      opt.value = String(v.id);
      opt.dataset.available = String(v.available);
      if (!v.available) opt.disabled = true;
      const sizeLabel = v.options?.[sizeIdx] ?? '';
      opt.textContent = v.available ? sizeLabel : `${sizeLabel} — ${soldOutSuffix}`;
      select.appendChild(opt);
    });

    select.addEventListener('change', () => this.#onSizeChange());

    // Insert before the ATC button. `insertBefore(node, null)` appends, so a
    // card without one still ends up with the select in the right place.
    const anchor = form.querySelector('[data-upsell-atc]');
    form.insertBefore(label, anchor);
    form.insertBefore(select, anchor);
  }

  #findSizeIndex(data) {
    const options = data.options || [];
    for (let i = 0; i < options.length; i++) {
      if (SIZE_NAMES.includes(normalizeOptionName(options[i]))) return i;
    }
    // Fallback: if there's only one option and multiple variants, that option must be the size.
    if (options.length === 1 && (data.variants || []).length > 1) return 0;
    // Fallback 2: detect by values shaped like sizes (S, M, L, XL, 36, 38, ...).
    const sizePattern = /^(x{0,3}[sml]|\d{2,3})$/i;
    for (let i = 0; i < options.length; i++) {
      const values = (data.variants || []).map((v) => String(v.options?.[i] || '').trim()).filter(Boolean);
      if (values.length && values.every((val) => sizePattern.test(val))) return i;
    }
    return -1;
  }

  #thumbUrl(url, size) {
    if (!url) return url;
    return url.replace(/(\.[^.\?]+)(\?|$)/, `_${size}x$1$2`);
  }

  #formatMoney(cents) {
    const value = (cents / 100).toLocaleString('sr-RS', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
    return `${value} RSD`;
  }
}

if (!customElements.get('color-upsell')) {
  customElements.define('color-upsell', ColorUpsell);
}

export { ColorUpsell };
